import { defineStore } from 'pinia'
import { streamStore } from './stream'
import { appStore } from './app'
import mqtt, { type MqttClient, type IClientOptions } from 'mqtt'
import log from '@/utils/useLogger'
import createJobId, { type COMMANDS } from '@/utils/createJobId'
import { useUserStore } from './userStore'
import { useProjectsStore } from './projectStore'
import { useTokenManager } from '@/composables/useTokenManager'
import { useMissionStore } from './mission'
import { uuid } from '@/helpers'
import type { TakeoverRequest } from '@/composables/Device/DeviceTakeover'
import { captureError } from '@/observability/sentry'
import { enrichCommandData } from '@/utils/enrichCommandData'

// Keys whose values are credentials and must never reach the logs.
const SENSITIVE_LOG_KEYS = new Set(['token', 'password', 'secret', 'authorization'])

/**
 * Only clients whose id starts with `idi.v2.` are counted as an operator being
 * present by the user-presence service. Keep this in sync with
 * SESSION_CLIENT_PREFIX in IDI-FLY-Backend/user-presence/layers/presence_store.py.
 */
const SESSION_CLIENT_PREFIX = 'idi.v2'

/** Where the per-tab client nonce is kept. See buildClientId. */
const CLIENT_NONCE_STORAGE_KEY = 'idi.mqtt.clientNonce'

/**
 * Build an MQTT client id of the form `{kind}.{userId}.{nonce}`.
 *
 * Dot delimited, and parsed by splitting on dots, which is unambiguous
 * because a Cognito sub is a UUID (hex and hyphens, no dots). The old format
 * was `idi-{userName}-{nonce}`, which carried the display name instead of the
 * id and could not be parsed at all once a userName contained a hyphen. That
 * is why lifecycle events could not be attributed to a user.
 *
 * The nonce is stored in sessionStorage, so it is stable across reloads of the
 * same tab (letting `clean: false` actually resume a session) but different in
 * every tab (so two tabs never collide on one client id, which would make the
 * broker kick them off in turn and create the very false-offline this ticket
 * is fixing).
 */
function buildClientId(kind: 'session' | 'device', userId: string): string {
  let nonce: string
  try {
    nonce = window.sessionStorage.getItem(CLIENT_NONCE_STORAGE_KEY) || ''
    if (!nonce) {
      nonce = uuid(10)
      window.sessionStorage.setItem(CLIENT_NONCE_STORAGE_KEY, nonce)
    }
  } catch {
    // Private mode or storage disabled: fall back to a per-call nonce. The
    // session simply will not resume across reloads.
    nonce = uuid(10)
  }
  const prefix = kind === 'session' ? SESSION_CLIENT_PREFIX : 'idi.device'
  return `${prefix}.${userId}.${nonce}`
}

/** Retained topic carrying a user's last known online/offline state. */
function presenceStateTopic(organisationId: string, userId: string): string {
  return `presence/${organisationId}/${userId}`
}

/** Non-retained topic carrying periodic liveness and activity. */
function presenceHeartbeatTopic(organisationId: string, userId: string): string {
  return `presence/${organisationId}/${userId}/heartbeat`
}

/** Capped exponential backoff with jitter. Never gives up. */
function reconnectDelayMs(attempt: number): number {
  const base = Math.min(1000 * Math.pow(2, attempt), 30000)
  return base + Math.floor(Math.random() * 1000)
}

/**
 * Recursively clone a value, replacing the values of sensitive keys
 * (tokens, passwords) with '[REDACTED]' so command logging never leaks
 * credentials.
 */
function redactSensitive(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactSensitive)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) =>
        SENSITIVE_LOG_KEYS.has(key.toLowerCase())
          ? [key, '[REDACTED]']
          : [key, redactSensitive(val)],
      ),
    )
  }
  return value
}

/**
 * Turn an outbound MQTT message string into a loggable, redacted payload so we
 * capture the actual command values (waypoints, height, gimbal angles, etc.)
 * and not just the topic. Expands the nested `state.desired.data` field, which
 * several commands double-stringify, and falls back to the raw string when the
 * message is not JSON.
 */
function buildLoggablePayload(message: string): unknown {
  if (!message) return message
  try {
    const parsed = JSON.parse(message)
    const desired = parsed?.state?.desired
    if (desired && typeof desired.data === 'string') {
      try {
        desired.data = JSON.parse(desired.data)
      } catch {
        // leave `data` as-is when it is not JSON
      }
    }
    return redactSensitive(parsed)
  } catch {
    return message
  }
}

/**
 * FLY3D-1029: MQTT is split across two clients.
 *
 * `client` is the DEVICE client. It carries telemetry, shadows and commands
 * for the currently selected RC + dock, and is torn down and rebuilt every
 * time the operator switches device. Every command action in this store
 * publishes through it.
 *
 * `sessionClient` is the SESSION client. It carries the topics that belong to
 * the user rather than to a device (takeover, assignment changes, revocation,
 * logout, presence) and is created once at login, then left alone.
 *
 * They used to be one client, which is why an operator could be reported
 * offline: rebuilding it on every device switch took the `ping/{userId}`
 * subscription down with it for several seconds, inside the old 5 second probe
 * window. Identity topics must not depend on which drone is selected.
 */
export type SessionConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting'

interface MqttState {
  client: MqttClient | null
  sessionClient: MqttClient | null
  sessionClientId: string | null
  sessionState: SessionConnectionState
  /**
   * True once the session client has been down long enough that the operator
   * can no longer be assumed to be reachable. Drives the persistent
   * "you will not receive takeover requests" banner. Previously the app gave
   * up after three attempts and said nothing beyond a transient toast.
   */
  realtimeDegraded: boolean
  sessionReconnectAttempts: number
  appState?: string
  isGoToLocationActive?: boolean
  reconnectAttempts: number
  messageHandler: ((topic: string, message: Buffer) => void) | null
  takeoverRequest: TakeoverRequest | null
  setUpHandler: (() => void) | null
}

export const mqttStore = defineStore('mqtt', {
  state: (): MqttState => ({
    client: null,
    sessionClient: null,
    sessionClientId: null,
    sessionState: 'idle',
    realtimeDegraded: false,
    sessionReconnectAttempts: 0,
    appState: 'IDLE',
    isGoToLocationActive: false,
    messageHandler: null,
    reconnectAttempts: 0,
    takeoverRequest: null,
    setUpHandler: null,
  }),

  actions: {
    endConnection(): void {
      this.client?.end()
    },
    getDeviceName() {
      const { allStreams } = streamStore()
      return allStreams[1].device_name
    },
    async publishMessage(topic: string, message: string, qos: 0 | 1 | 2 = 1, command?: string) {
      try {
        const { getValidToken } = useTokenManager()
        const token = await getValidToken()
        if (!token) throw new Error('failed to get valid token')

        const endpoint = import.meta.env.VITE_APP_IOT_ENPOINT
        const encodedTopic = encodeURIComponent(topic)
        const encodedToken = encodeURIComponent(token)

        const url = `https://${endpoint}:443/topics/${encodedTopic}?qos=${qos}&x-amz-customauthorizer-name=DashboardSecuredAuthoriser&token-key-name=${encodedToken}`

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: message,
        })

        if (!response.ok) {
          log.error('Failed to publish message to mqtt', response.statusText)
          throw new Error('Failed to publish message to mqtt')
        }
        const payload = buildLoggablePayload(message)
        if (command) log.info(`Published ${command} to ${topic}`, payload)
        else log.info(`Published message to ${topic}`, payload)
        return response.ok
      } catch (err) {
        const { addNotifications } = appStore()
        addNotifications({
          notification: 'Failed to publish message to mqtt',
          type: 'error',
          id: crypto.randomUUID(),
        })
        captureError(err, { context: 'publishMessage', topic })
        return false
      }
    },
    // Credentials for opening a virtual-stick MQTT connection from a Web Worker
    // (same broker/auth as the main client). Token/username logic stays
    // centralised here; the worker uses these to connect and publish off-thread.
    async getMqttCredentials(): Promise<{ url: string; username: string; token: string } | null> {
      const { getValidToken } = useTokenManager()
      const { userName } = useUserStore()
      const token = await getValidToken()
      if (!token || !userName) return null
      return { url: import.meta.env.VITE_APP_MQTT_URL, username: userName, token }
    },
    createMqttMessage(data: string, command: string) {
      const message = {
        state: {
          desired: {
            mission_command: command,
            data: data,
          },
        },
      }
      return JSON.stringify(message)
    },
    sendMqttMessage(topic: string, message: string, command: string, qos: 0 | 1 | 2 = 1) {
      const mqttClient = this.client
      if (!mqttClient) return
      const payload = buildLoggablePayload(message)
      mqttClient.publish(topic, message, { qos }, (err) => {
        if (err) log.error(`Failed to publish ${command} to ${topic}:`, err)
        else log.info(`published ${command} to ${topic}`, payload)
      })
    },
    /**
     * Connect (or reconnect) the session client.
     *
     * Idempotent: calling it while a healthy client exists is a no-op, so
     * App.vue can call it freely on mount and on user change.
     *
     * Three things happen here that the old single client never did:
     *
     * 1. `clean: false` with a per-tab stable client id, so a brief reconnect
     *    resumes the session and the broker delivers queued QoS 1 messages
     *    instead of dropping them.
     * 2. A Last Will and Testament on the retained presence topic. If this
     *    browser dies ungracefully (lid closed, wifi dropped, tab killed) the
     *    BROKER publishes the offline state on our behalf. Nothing depends on
     *    the dying client managing to send anything.
     * 3. A retained `online` publish on connect, so any dashboard subscribing
     *    to `presence/{org}/+` learns the state immediately on subscribe
     *    rather than having to ask and wait.
     */
    async connectSession(setUpHandler?: () => void): Promise<void> {
      if (setUpHandler) this.setUpHandler = setUpHandler

      if (this.sessionClient?.connected) {
        log.debug('session client already connected')
        return
      }
      if (this.sessionState === 'connecting') {
        log.debug('session client already connecting')
        return
      }

      const { userName, userId, organisationId } = useUserStore()
      if (!userName || !userId || !organisationId) {
        log.warn('cannot connect session client: missing user identity')
        return
      }

      this.teardownSessionClient()
      this.sessionState = 'connecting'

      try {
        const { getValidToken } = useTokenManager()
        const token = await getValidToken(true)
        if (!token) throw new Error('failed to get valid token')

        const clientId = buildClientId('session', userId)
        const stateTopic = presenceStateTopic(organisationId, userId)
        this.sessionClientId = clientId

        this.sessionClient = mqtt.connect(import.meta.env.VITE_APP_MQTT_URL, {
          clientId,
          username: userName,
          password: token,
          protocolVersion: 5,
          protocolId: 'MQTT',
          keepalive: 60,
          reschedulePings: true,
          resubscribe: true,
          reconnectPeriod: 1000,
          connectTimeout: 30 * 1000,
          // Persistent session: the broker holds our subscriptions and queued
          // QoS 1 messages across a short drop.
          clean: false,
          will: {
            topic: stateTopic,
            payload: JSON.stringify({
              state: 'offline',
              userId,
              clientId,
              source: 'lwt',
            }) as unknown as Buffer,
            qos: 1,
            retain: true,
          },
        } as IClientOptions)

        const sessionClient = this.sessionClient
        if (!sessionClient) return

        // Arm the degraded warning now, not only on a later 'offline' event.
        // A client that never manages to connect at all emits neither
        // 'offline' nor 'close', and would otherwise fail silently.
        this.markDegradedSoon()

        sessionClient.on('connect', () => {
          this.sessionState = 'connected'
          this.sessionReconnectAttempts = 0
          this.realtimeDegraded = false
          log.info('Session client connected', clientId)

          sessionClient.subscribe(`logout/${userName}`)
          sessionClient.subscribe(`device-group-takeover-request/${userId}`)
          sessionClient.subscribe(`device-group-takeover-response/${userId}`)
          sessionClient.subscribe(`device-group-assignment-changed/${userId}`)
          sessionClient.subscribe(`annotation-shared/${userId}`)
          // FLY3D-914: sharing/ACL revocation pushes.
          sessionClient.subscribe(`revocation/${userId}`)
          // FLY3D-1029: retained presence for every colleague in the org.
          // Retained means state for all of them arrives on subscribe, with no
          // request/response and no timeout to lose.
          sessionClient.subscribe(`presence/${organisationId}/+`, { qos: 1 })

          this.publishPresenceState('online')

          if (this.setUpHandler) this.setUpHandler()
        })

        sessionClient.on('error', (error: Error) => {
          captureError(error, { context: 'session_client_error', clientId })
          const msg = typeof error === 'string' ? error : error.message
          if (msg.includes('Not authorized')) {
            // The token is baked into the password, so mqtt.js reconnecting on
            // its own would just replay the stale one. Tear down, refresh, and
            // retry -- indefinitely. The old code stopped after three attempts
            // and left the user logged in but permanently unreachable, which
            // is a strong candidate for the original incident.
            log.warn('Session client not authorized, refreshing token')
            this.scheduleSessionReconnect()
          }
        })

        sessionClient.on('offline', () => {
          log.warn('Session client offline')
          if (this.sessionState === 'connected') this.sessionState = 'reconnecting'
          this.markDegradedSoon()
        })

        sessionClient.on('close', () => {
          // Also clears 'connecting': leaving the state stuck there would make
          // connectSession() no-op forever on its idempotency guard.
          if (this.sessionState !== 'idle') this.sessionState = 'reconnecting'
          this.markDegradedSoon()
        })
      } catch (err) {
        captureError(err, { context: 'connectSession' })
        this.scheduleSessionReconnect()
      }
    },

    /**
     * Publish the retained presence state for this user.
     *
     * Retained so late subscribers get it, QoS 1 so it is not lost in a
     * reconnect. Published over the MQTT client rather than the HTTP publish
     * helper because only MQTT can set the retain flag.
     */
    publishPresenceState(state: 'online' | 'offline'): void {
      const { userId, organisationId } = useUserStore()
      const sessionClient = this.sessionClient
      if (!sessionClient || !userId || !organisationId) return

      const payload = JSON.stringify({
        state,
        userId,
        clientId: this.sessionClientId,
        at: new Date().toISOString(),
      })
      sessionClient.publish(
        presenceStateTopic(organisationId, userId),
        payload,
        { qos: 1, retain: true },
        (err) => {
          if (err) log.error(`Failed to publish presence state ${state}`, err)
          else log.info(`Published retained presence state: ${state}`)
        },
      )
    },

    /**
     * Publish one heartbeat. Called on an interval by usePresence.
     *
     * `inputIdleMs` is a DURATION, not a timestamp, so a browser with a wrong
     * clock cannot corrupt the server's activity grading.
     */
    publishPresenceHeartbeat(payload: {
      inputIdleMs: number
      activeDeviceGroupIds: string[]
      isFlying: boolean
      appVersion?: string
    }): void {
      const { userId, organisationId } = useUserStore()
      const sessionClient = this.sessionClient
      if (!sessionClient?.connected || !userId || !organisationId) return

      // QoS 0: a lost heartbeat is superseded 20 seconds later, and the
      // server treats a gap as `stale`, never as offline.
      sessionClient.publish(
        presenceHeartbeatTopic(organisationId, userId),
        JSON.stringify({ clientId: this.sessionClientId, ...payload }),
        { qos: 0 },
      )
    },

    /**
     * Flag the real-time connection as degraded if it is still down shortly
     * from now. Delayed so an ordinary sub-second reconnect does not flash a
     * scary banner at the operator.
     */
    markDegradedSoon(): void {
      if (this.realtimeDegraded) return
      window.setTimeout(() => {
        if (!this.sessionClient?.connected) {
          this.realtimeDegraded = true
          log.warn('Real-time session degraded: takeover requests will not arrive')
        }
      }, 10000)
    },

    /** Reconnect the session client with capped backoff. Never gives up. */
    scheduleSessionReconnect(): void {
      const delay = reconnectDelayMs(this.sessionReconnectAttempts)
      this.sessionReconnectAttempts += 1
      this.sessionState = 'reconnecting'
      this.markDegradedSoon()
      this.teardownSessionClient()

      log.info(
        `Reconnecting session client in ${delay}ms (attempt ${this.sessionReconnectAttempts})`,
      )
      window.setTimeout(() => {
        this.connectSession().catch((err) =>
          log.error('Session reconnect attempt failed:', err),
        )
      }, delay)
    },

    /** Drop the socket without touching retained state. */
    teardownSessionClient(): void {
      if (!this.sessionClient) return
      try {
        this.sessionClient.removeAllListeners()
        this.sessionClient.end(true)
      } catch (err) {
        log.warn('error tearing down session client', err)
      }
      this.sessionClient = null
    },

    /**
     * Graceful sign-off. Publishes retained `offline` BEFORE closing, so
     * colleagues see a clean departure rather than waiting for the keepalive
     * to expire. Called on logout.
     */
    async disconnectSession(): Promise<void> {
      if (this.sessionClient?.connected) {
        this.publishPresenceState('offline')
        // Give the retained publish a moment to leave the socket. The LWT is
        // the backstop if it does not.
        await new Promise((resolve) => window.setTimeout(resolve, 250))
      }
      this.teardownSessionClient()
      this.sessionState = 'idle'
      this.sessionClientId = null
      this.realtimeDegraded = false
      this.sessionReconnectAttempts = 0
    },

    async connectToShadows(
      selectedRC: string,
      selectedDock: string,
      setUpHandler?: () => void,
    ): Promise<void> {
      try {
        // Store setUpHandler if provided
        if (setUpHandler) {
          this.setUpHandler = setUpHandler
        }
        if (this.client) {
          // Properly clean up existing client
          this.client.removeAllListeners()
          this.client.end(true)
          this.client = null
        }
        const { userName, userId } = useUserStore()
        const { selectedPortal } = appStore()
        if (!userName || !userId) return
        const { getValidToken } = useTokenManager()
        const token = await getValidToken(true)
        if (!token) throw new Error('failed to get valid token')
        this.client = mqtt.connect(import.meta.env.VITE_APP_MQTT_URL, {
          // `idi.device.` deliberately does NOT match the `idi.v2.` prefix the
          // presence service counts, so the churn of device switching never
          // shows up as the operator connecting and disconnecting.
          clientId: buildClientId('device', userId),
          reconnectPeriod: 1000,
          protocolVersion: 5,
          reschedulePings: true,
          keepalive: 60,
          resubscribe: true,
          clean: true,
          connectTimeout: 30 * 1000,
          protocolId: 'MQTT',
          password: token,
          username: userName,
        } as IClientOptions)
        const mqttClient = this.client
        if (!mqttClient) return
        mqttClient.on('connect', () => {
          this.reconnectAttempts = 0
          log.info('Real-time service Connected', selectedRC, selectedDock)
          // subscribe to slinktech topics
          if (selectedPortal !== '0000') {
            mqttClient.subscribe(`${selectedPortal}/PORTAL_STATUS`)
          }
          // NOTE (FLY3D-1029): the user-scoped topics (logout, takeover,
          // assignment-changed, annotation-shared, revocation) used to be
          // subscribed here. They now live on the session client, which
          // survives device switching. Do not add user-scoped topics to this
          // client: it is destroyed and rebuilt whenever selectedDevice
          // changes, and anything subscribed here goes dark for the duration.
          mqttClient.subscribe(`${selectedRC}/MEDIA_UPLOAD_EVENT`)
          mqttClient.subscribe(selectedRC + '/TELEMETRY', { qos: 0 })
          mqttClient.subscribe(`${selectedRC}/TELEMTRY_HIGH_FREQ`, { qos: 0 })
          if (selectedDock && selectedDock !== '0000') {
            mqttClient.subscribe(selectedDock + '/DOCK_STATE_DATA')
            mqttClient.subscribe(`$aws/things/${selectedDock}/DOCK_STATE/update/accepted`)
          }
          mqttClient.subscribe(`${selectedDock}/WEATHER_DATA`)
          mqttClient.subscribe(`$aws/things/${selectedRC}/shadow/name/RC_STATE/update/accepted`)
          mqttClient.subscribe(
            `$aws/things/${selectedRC}/shadow/name/RC_STATE/get/accepted`,
            function (err: Error | null) {
              if (!err) {
                mqttClient.publish(`$aws/things/${selectedRC}/shadow/name/RC_STATE/get`, '')
              }
            },
          )
          mqttClient.subscribe(`${selectedRC}/DEVICE_HEALTH`)
          mqttClient.subscribe(`${selectedDock}/DEVICE_HEALTH`)
          mqttClient.subscribe(
            `$aws/things/${selectedDock}/shadow/name/DOCK_COMMANDS/update/accepted`,
          )
          mqttClient.subscribe(
            `$aws/things/${selectedDock}/shadow/name/AUTOMATION_COMMAND/update/accepted`,
          )
          mqttClient.subscribe(
            `$aws/things/${selectedDock}/shadow/name/AUTOMATION_STATE/update/accepted`,
          )
          mqttClient.subscribe(
            `$aws/things/${selectedRC}/shadow/name/NOTIFICATIONS/update/accepted`,
          )
          mqttClient.subscribe(
            `$aws/things/${selectedDock}/shadow/name/NOTIFICATIONS/update/accepted`,
          )
          mqttClient.subscribe(
            `$aws/things/${selectedRC}/shadow/name/MISSION_COMMAND/update/accepted`,
          )
          mqttClient.subscribe(
            `$aws/things/${selectedRC}/shadow/name/COMMAND_STATE/update/documents`,
          )
          mqttClient.subscribe(
            `$aws/things/${selectedRC}/shadow/name/CAMERA_COMMAND/update/accepted`,
          )
          mqttClient.subscribe(
            `$aws/things/${selectedRC}/shadow/name/CAMERA_COMMANDS/update/accepted`,
          )
          mqttClient.subscribe(
            `$aws/things/${selectedRC}/shadow/name/CAMERA_CONFIG/update/accepted`,
          )
          mqttClient.subscribe(
            `$aws/things/${selectedRC}/shadow/name/VIRTUALSTICK_STATE/update/accepted`,
          )
          mqttClient.subscribe(`$aws/things/${selectedRC}/shadow/name/VIRTUALSTICK_COMMANDS/update`)
          mqttClient.subscribe(`$aws/things/${selectedRC}/shadow/name/CAMERA_STATE/update/accepted`)
          mqttClient.subscribe(`${selectedRC}/Audio_trigger`)
          mqttClient.subscribe(
            `$aws/things/${selectedRC}/shadow/name/CAMERA_STATE/get/accepted`,
            {},
            function (err?: Error | null) {
              if (!err) {
                mqttClient.publish(`$aws/things/${selectedRC}/shadow/name/CAMERA_STATE/get`, '')
              }
            },
          )
          if (selectedDock && selectedDock !== '0000') {
            mqttClient.subscribe(
              `$aws/things/${selectedDock}/shadow/name/DOCK_STATE/get/accepted`,
              {},
              function (err?: Error | null) {
                if (!err) {
                  mqttClient.publish(`$aws/things/${selectedDock}/shadow/name/DOCK_STATE/get`, '')
                }
              },
            )
            mqttClient.subscribe(
              `$aws/things/${selectedDock}/shadow/name/DOCK_STATE/update/accepted`,
            )
          }
          // subscribe to active job telemetry
          mqttClient.subscribe(`${selectedRC}/ACTIVE_JOBS_TELEMETRY`, { qos: 0 })
          if (this.setUpHandler) {
            this.setUpHandler()
          }
        })
        mqttClient.on('error', (error: Error) => {
          captureError(error, { context: 'mqtt_client_error', selectedRC, selectedDock })
          const msg = typeof error === 'string' ? error : error.message
          if (msg.includes('Not authorized')) {
            log.warn('MQTT Client Not authorized, attempting to refresh token')

            // Stop mqtt.js auto reconnect loop for this client
            mqttClient.removeAllListeners()
            mqttClient.end(true)
            this.client = null

            // FLY3D-1029: retry indefinitely with capped backoff. This used to
            // stop after three attempts and only raise a transient toast,
            // which left the operator logged in with a dead real-time
            // connection and no lasting indication of it.
            const backoffMs = reconnectDelayMs(this.reconnectAttempts)
            this.reconnectAttempts++
            if (this.reconnectAttempts === 3) {
              const { addNotifications } = appStore()
              addNotifications({
                notification:
                  'Real-time connection to the aircraft is having trouble. Still retrying.',
                type: 'error',
                id: crypto.randomUUID(),
              })
            }
            setTimeout(() => {
              // reconnect to the shadows
              this.connectToShadows(
                selectedRC,
                selectedDock,
                this.setUpHandler || undefined,
              ).catch((err) => {
                log.error('Reconnection attempt failed:', err)
              })
            }, backoffMs)
          }
        })
        mqttClient.on('offline', () => {
          log.warn('MQTT Client Offline')
        })
        mqttClient.on('disconnect', () => {
          log.warn('MQTT Client Disconnected')
        })
      } catch (err) {
        captureError(err, { context: 'connectToShadows', selectedRC, selectedDock })
      }
    },

    disconnectShadows(selectedRC?: string, selectedDock?: string): void {
      if (!selectedRC || !selectedDock) {
        const { selectedRC: rc, selectedDock: dock } = appStore()
        selectedRC = rc
        selectedDock = dock
      }
      const { selectedPortal } = appStore()
      const mqttClient = this.client
      log.info('disconnecting selectedRC', selectedRC)
      if (!mqttClient) return

      mqttClient.unsubscribe(`${selectedRC}/MEDIA_UPLOAD_EVENT`)
      mqttClient.unsubscribe(`${selectedPortal}/PORTAL_STATUS`)
      // FLY3D-1029: user-scoped topics are NOT unsubscribed here any more.
      // They belong to the session client, and tearing them down on a device
      // switch is exactly what made a logged-on operator look offline.
      mqttClient.unsubscribe(selectedRC + '/TELEMETRY')
      mqttClient.unsubscribe(selectedRC + '/TELEMTRY_HIGH_FREQ')
      mqttClient.unsubscribe(selectedDock + '/DOCK_STATE_DATA')
      mqttClient.unsubscribe(`$aws/things/${selectedDock}/DOCK_STATE/update/accepted`)
      mqttClient.unsubscribe(`${selectedDock}/WEATHER_DATA`)
      mqttClient.unsubscribe(`$aws/things/${selectedRC}/shadow/name/RC_STATE/update/accepted`)
      mqttClient.unsubscribe(`$aws/things/${selectedRC}/shadow/name/RC_STATE/get/accepted`)
      mqttClient.unsubscribe(`${selectedRC}/DEVICE_HEALTH`)
      mqttClient.unsubscribe(`${selectedDock}/DEVICE_HEALTH`)
      mqttClient.unsubscribe(
        `$aws/things/${selectedDock}/shadow/name/DOCK_COMMANDS/update/accepted`,
      )
      mqttClient.unsubscribe(
        `$aws/things/${selectedDock}/shadow/name/AUTOMATION_COMMAND/update/accepted`,
      )
      mqttClient.unsubscribe(
        `$aws/things/${selectedDock}/shadow/name/AUTOMATION_STATE/update/accepted`,
      )
      mqttClient.unsubscribe(`$aws/things/${selectedRC}/shadow/name/NOTIFICATIONS/update/accepted`)
      mqttClient.unsubscribe(
        `$aws/things/${selectedDock}/shadow/name/NOTIFICATIONS/update/accepted`,
      )
      mqttClient.unsubscribe(
        `$aws/things/${selectedRC}/shadow/name/MISSION_COMMAND/update/accepted`,
      )
      mqttClient.unsubscribe(`$aws/things/${selectedRC}/shadow/name/COMMAND_STATE/update/documents`)
      mqttClient.unsubscribe(`$aws/things/${selectedRC}/shadow/name/CAMERA_COMMAND/update/accepted`)
      mqttClient.unsubscribe(
        `$aws/things/${selectedRC}/shadow/name/CAMERA_COMMANDS/update/accepted`,
      )
      mqttClient.unsubscribe(`$aws/things/${selectedRC}/shadow/name/CAMERA_CONFIG/update/accepted`)
      mqttClient.unsubscribe(
        `$aws/things/${selectedRC}/shadow/name/VIRTUALSTICK_STATE/update/accepted`,
      )
      mqttClient.unsubscribe(`$aws/things/${selectedRC}/shadow/name/VIRTUALSTICK_COMMANDS/update`)
      mqttClient.unsubscribe(`$aws/things/${selectedRC}/shadow/name/CAMERA_STATE/update/accepted`)
      mqttClient.unsubscribe(`$aws/things/${selectedRC}/shadow/name/CAMERA_STATE/get/accepted`)
      mqttClient.unsubscribe(`$aws/things/${selectedDock}/shadow/name/DOCK_STATE/get/accepted`)
      mqttClient.unsubscribe(`$aws/things/${selectedDock}/shadow/name/DOCK_STATE/update/accepted`)
      mqttClient.unsubscribe(`${selectedRC}/Audio_trigger`)
      mqttClient.unsubscribe(`${selectedRC}/ACTIVE_JOBS_TELEMETRY`)
      if (this.messageHandler && typeof this.messageHandler === 'function') {
        mqttClient.off('message', this.messageHandler)
      }
    },

    async sendNotification(message: string, status: string, topic?: string): Promise<void> {
      const { selectedRC } = appStore()
      const url = `$aws/things/${selectedRC}/shadow/name/NOTIFICATIONS/update`
      const payload = JSON.stringify({
        state: { reported: { notification: message, type: status } },
      })
      await this.publishMessage(url, payload)
    },

    async toggleVirtualSticks(val: boolean) {
      const command = val ? 'ENABLE_VIRTUALSTICK' : 'DISABLE_VIRTUALSTICK'
      await this.sendCommand(command, { currentSpeedLevel: 1.0 })
    },
    async cameraControl(command: COMMANDS, prop: string, payload: string | number): Promise<void> {
      const params = {
        [prop]: payload,
      }
      await this.sendCommand(command, params)
    },
    async updateGimbalCommand(payload: any, ignore: any) {
      const { selectedRC } = appStore()
      const { userName, userId } = useUserStore()
      const data = enrichCommandData({
        pitch: payload.tiltVal,
        roll: payload.rollVal,
        yaw: payload.panVal,
        duration: payload.duration || 1,
        mode: payload.mode || 'RELATIVE_ANGLE',
        pitchIgnored: ignore.tilt,
        rollIgnored: ignore.roll,
        yawIgnored: ignore.pan,
        userName,
        userId,
      })
      const message = JSON.stringify({
        state: {
          desired: {
            data: JSON.stringify(data),
          },
        },
      })
      await this.publishMessage(
        `$aws/things/${selectedRC}/shadow/name/GIMBAL_COMMAND/update`,
        message,
      )
    },
    publishDefaultCommand(topic: string, data: any): void {
      const { selectedRC } = appStore()
      if (!this.client) return

      // NOTE: intentionally no success log here. This runs on the hot path
      // (virtual-stick controllers publish 5-25x/s); logging every publish to
      // the console + MQTT was starving the renderer main thread. Errors only.
      this.client.publish(`${selectedRC}/${topic}`, data, (err) => {
        if (err) log.error(`Failed to publish default command to ${selectedRC}:`, err)
      })
    },
    async publishCameraConfig(prop: string, payload: string): Promise<void> {
      const { selectedRC } = appStore()
      const { userName, userId } = useUserStore()
      const message = JSON.stringify({
        state: { desired: { [prop]: payload, data: enrichCommandData({ userName, userId }) } },
      })
      await this.publishMessage(
        '$aws/things/' + selectedRC + '/shadow/name/CAMERA_CONFIG/update',
        message,
      )
    },
    async sendDockCommand(dock_command: string): Promise<void> {
      const { selectedDock } = appStore()
      const { userName, userId } = useUserStore()
      const { getValidToken } = useTokenManager()
      const token = await getValidToken()
      if (!token) return log.warn('cannot send command: missing / expired token ')
      const message = {
        state: {
          desired: {
            job_id: createJobId(dock_command),
            command: dock_command,
            timeout: 30000,
            token,
            parameters: {},
            timestamp: Math.floor(Date.now() / 1000),
            source: 'DASHBOARD',
            data: enrichCommandData({ userName, userId }),
          },
        },
      }
      await this.publishMessage(
        `$aws/things/${selectedDock}/shadow/name/DOCK_COMMANDS/update`,
        JSON.stringify(message),
        1,
        dock_command,
      )
    },
    async sendActiveDroneCommand(val: 0 | 1): Promise<void> {
      const { selectedDock } = appStore()
      const { userName, userId } = useUserStore()
      const message = {
        state: {
          desired: {
            dock_command: 'SET_ACTIVE_DRONE',
            id: 55,
            value: val,
            data: enrichCommandData({ userName, userId }),
          },
        },
      }
      await this.publishMessage(
        `$aws/things/${selectedDock}/shadow/name/DOCK_COMMANDS/update`,
        JSON.stringify(message),
      )
    },
    async startAutomationCmd(command: string, type: 'GTL' | 'MISSION'): Promise<void> {
      const { selectedDock } = appStore()
      const { userName, userId } = useUserStore()
      const { getValidToken } = useTokenManager()
      const token = await getValidToken()
      if (!token) return log.warn('cannot send command: missing / expired token ')
      const message = {
        state: {
          desired: {
            job_id: createJobId(command),
            automation_command: command,
            token,
            mode: 'DASHBOARD',
            type,
            data: enrichCommandData({ userName, userId }),
          },
        },
      }
      await this.publishMessage(
        `$aws/things/${selectedDock}/shadow/name/AUTOMATION_COMMAND/update`,
        JSON.stringify(message),
      )
    },

    async sendCommand(command: COMMANDS, params?: {}): Promise<string> {
      const { userName, userId } = useUserStore()
      if (!userName || !userId) {
        captureError(new Error('Cannot send command: missing userName or userId'), {
          context: 'sendCommand',
          command,
        })
        return ''
      }
      const job_id = createJobId(command)
      // Active project context (FLY3D-938) — device-side captures/uploads tag
      // resources with it; devices ignore unknown fields.
      const { activeProjectId } = useProjectsStore()
      const data: any = {
        job_id,
        userName,
        userId,
        ...(activeProjectId ? { projectID: activeProjectId } : {}),
        ...params,
      }
      if (command === 'TAKEOFF') {
        data.mode = 'APP'
        const { dockAmsl } = appStore()
        data.terrain_height = dockAmsl
      }
      const message = this.createMqttMessage(JSON.stringify(data), command)
      const topic = `$aws/things/${this.getDeviceName()}/shadow/name/MISSION_COMMAND/update`
      await this.publishMessage(topic, message, 1, command)
      return job_id
    },
    // MAVLink aircraft commands (FLY3D-965). Thin wrappers over sendCommand;
    // the RC bridge translates them to MAVLink (DO_SET_MODE, COMMAND_LONG,
    // DO_SET_MISSION_CURRENT, DO_MOUNT_CONTROL).
    async setFlightMode(mode: string, customMode: number, airframe: string): Promise<string> {
      return this.sendCommand('SET_MODE', { mode, custom_mode: customMode, airframe })
    },
    async doMavlinkAction(action: string, mavCmd: number, params: number[]): Promise<string> {
      return this.sendCommand('DO_ACTION', { action, mav_cmd: mavCmd, params })
    },
    async setWaypoint(seq: number): Promise<string> {
      return this.sendCommand('SET_WP', { seq })
    },
    async setMount(mountMode: number): Promise<string> {
      return this.sendCommand('SET_MOUNT', { mount_mode: mountMode })
    },
    // Orbit a point (MAV_CMD_DO_ORBIT). The bridge sends COMMAND_INT in frame
    // MAV_FRAME_GLOBAL_RELATIVE_ALT_INT: lat/lon degE7, alt relative-to-home.
    // Signed radius sets direction (+CW / -CCW).
    async doOrbit(params: {
      lat: number
      lon: number
      alt: number
      radius: number
      velocity: number
      yaw_behavior: number
      turns: number
    }): Promise<string> {
      return this.sendCommand('DO_ORBIT', { mav_cmd: 34, frame: 6, ...params })
    },
    async cancelJob(target: string = 'all'): Promise<void> {
      const command = 'CANCEL_JOB'
      const { userName, userId } = useUserStore()
      const data = enrichCommandData({
        job_id: createJobId(command),
        target,
        userName,
        userId,
      })
      const message = this.createMqttMessage(JSON.stringify(data), command)
      const topic = `$aws/things/${this.getDeviceName()}/shadow/name/MISSION_COMMAND/update`
      await this.publishMessage(topic, message, 1, command)
    },
    async runMission(): Promise<void> {
      const { missionToLaunch, resetMissionState } = useMissionStore()
      const { organisationId, userName, userId } = useUserStore()
      const { activeProjectId } = useProjectsStore()
      if (!missionToLaunch) throw new Error('No mission was provided')

      let data: Record<string, any>
      if (missionToLaunch.type === 's3') {
        data = enrichCommandData({
          job_id: createJobId('RUN_MISSION'),
          missionName: missionToLaunch.missionName,
          organisationID: organisationId,
          userID: userId,
          userName,
          type: 's3',
        })
      } else {
        data = enrichCommandData({
          job_id: createJobId('RUN_MISSION'),
          missionID: missionToLaunch.id,
          userName,
          userId,
          organisationID: organisationId ?? undefined,
        })
      }
      // Active project context (FLY3D-938): flight-generated media syncs back
      // into the project the mission was launched from.
      if (activeProjectId) data.projectID = activeProjectId

      const message = this.createMqttMessage(JSON.stringify(data), 'RUN_MISSION')
      const topic = `$aws/things/${this.getDeviceName()}/shadow/name/MISSION_COMMAND/update`
      await this.publishMessage(topic, message, 1, 'RUN_MISSION')
      resetMissionState()
    },
    async bindDevice(command: string, data: string | null, id: string): Promise<void> {
      const { userName, userId } = useUserStore()
      const payload = JSON.stringify({
        state: {
          desired: {
            mission_command: command,
            data: { userName, userId, ...(data ? JSON.parse(data) : {}) },
          },
        },
      })
      await this.publishMessage(`$aws/things/${id}/shadow/name/MISSION_COMMAND/update`, payload)
    },
    async goToLocation(height: number, speed: number): Promise<void> {
      const { gtlPoints } = appStore()
      if (this.appState === 'INTERRUPTED' && this.isGoToLocationActive) {
        await this.sendCommand('STOP')
      }

      const { dockAmsl } = appStore()
      const waypoints = [
        {
          latitude: gtlPoints[0][1],
          longitude: gtlPoints[0][0],
          altitude: height,
          speed: speed,
          terrain_height: dockAmsl,
        },
        {
          latitude: gtlPoints[1][1],
          longitude: gtlPoints[1][0],
          altitude: height,
          speed: speed,
          terrain_height: dockAmsl,
        },
      ]

      await this.sendCommand('GOTOLOCATION', { waypoints })
    },
  },
})
